/**
 * Výsledek rozboru podkladů: co se ze závěrek přečetlo, předběžná metoda
 * a doplňující dotazy.
 *
 * Čísla jsou jen ta, která přečetl parser výkazů. Dotazy přicházejí ze
 * serveru z pevného katalogu. Odpověď nalezenou v zadání klient vidí
 * i s citací a může ji změnit, nic se nepřebírá bez jeho potvrzení.
 */
import React from 'react';
import { NavrhHodnoty } from './NavrhHodnoty';

const tis = v => (v === null || v === undefined ? 'nepřečteno'
  : `${Math.round(v).toLocaleString('cs-CZ')} tis. Kč`);
const proc = v => (v === null || v === undefined ? 'nelze spočítat'
  : `${v.toFixed(1).replace('.', ',')} %`);

function Radek({ label, hodnota }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.3rem 0', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: '0.88rem' }}>
      <span style={{ color: 'var(--text-dim)' }}>{label}</span>
      <span style={{ color: 'var(--star-white)', fontWeight: 500, textAlign: 'right' }}>{hodnota}</span>
    </div>
  );
}

function Karta({ nadpis, children }) {
  return (
    <div style={{ background: 'var(--purple-deep)', borderRadius: 12, padding: '1rem 1.1rem', marginBottom: '1rem' }}>
      <div style={{ fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.6rem' }}>{nadpis}</div>
      {children}
    </div>
  );
}

function Odpoved({ dotaz, hodnota, onZmena }) {
  if (dotaz.typ === 'volba') {
    return (
      <select value={hodnota ?? ''} onChange={e => onZmena(e.target.value || null)}>
        <option value="">Vyberte</option>
        {dotaz.moznosti.map(m => <option key={m.hodnota} value={m.hodnota}>{m.popis}</option>)}
      </select>
    );
  }
  if (dotaz.typ === 'ano_ne') {
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {[[true, 'Ano'], [false, 'Ne']].map(([h, popis]) => (
          <button key={popis} type="button"
            className={hodnota === h ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.4rem 1.2rem' }}
            onClick={() => onZmena(hodnota === h ? null : h)}>{popis}</button>
        ))}
      </div>
    );
  }
  return (
    <input type="number" min="0" inputMode="numeric" value={hodnota ?? ''}
      placeholder={dotaz.jednotka || 'Kč'}
      onChange={e => onZmena(e.target.value === '' ? null : Number(e.target.value))} />
  );
}

export function RozborPanel({ rozbor, odpovedi, citace, chybejiciPovinne, onZmena, prepocitavam }) {
  const vykaz = rozbor.vykaz;
  const metoda = rozbor.metoda;
  return (
    <div>
      {(rozbor.soubory || []).length > 0 && (
        <Karta nadpis="Co jsme ze závěrek přečetli">
          {rozbor.soubory.map(s => (
            <div key={s.nazev} style={{ fontSize: '0.85rem', marginBottom: '0.35rem', color: s.precteno ? 'var(--star-white)' : 'var(--text-dim)' }}>
              {s.precteno ? '✓' : '·'} {s.nazev}
              {s.precteno && s.rok ? `, rok ${s.rok}` : ''}
              {s.precteno && s.obsah?.length ? `, ${s.obsah.join(' a ')}` : ''}
              {!s.precteno && s.duvod ? ` (${s.duvod})` : ''}
            </div>
          ))}
          {vykaz && (
            <div style={{ marginTop: '0.6rem' }}>
              <Radek label={`Tržby${vykaz.rok ? ` ${vykaz.rok}` : ''}`} hodnota={tis(vykaz.trzby)} />
              <Radek label="Tržby minulého roku" hodnota={tis(vykaz.hodnoty?.trzby_min)} />
              <Radek label="EBITDA (provozní výsledek a odpisy)" hodnota={tis(vykaz.ebitda)} />
              <Radek label="EBITDA marže" hodnota={proc(vykaz.marze_ebitda)} />
              <Radek label="Aktiva celkem" hodnota={tis(vykaz.hodnoty?.aktiva_celkem)} />
              <Radek label="Vlastní kapitál" hodnota={tis(vykaz.hodnoty?.vlastni_kapital)} />
              <Radek label="Úvěry" hodnota={tis(vykaz.uverove_zavazky)} />
            </div>
          )}
        </Karta>
      )}

      {(rozbor.chybi || []).length > 0 && (
        <div className="warning-message" style={{ marginBottom: '1rem' }}>
          {rozbor.chybi.map(c => <div key={c}>{c}</div>)}
        </div>
      )}

      {metoda && (
        <Karta nadpis={`${metoda.predbezna ? 'Předběžně zvolená metoda' : 'Zvolená metoda'}: ${metoda.nazev}`}>
          <p style={{ fontSize: '0.88rem', color: 'var(--star-white)', margin: '0 0 0.4rem' }}>
            Počítá se jako násobek: {metoda.zaklad}.
          </p>
          {metoda.duvody.map(d => <p key={d} style={{ fontSize: '0.85rem', color: 'var(--text-dim)', margin: '0 0 0.3rem' }}>{d}</p>)}
          {metoda.muze_zmenit?.map(d => <p key={d} style={{ fontSize: '0.85rem', color: 'var(--purple-glow)', margin: '0.3rem 0 0' }}>{d}</p>)}
          {metoda.odmitnute?.length > 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0.4rem 0 0' }}>
              Nepoužili jsme: {metoda.odmitnute.join('; ')}.
            </p>
          )}
          {metoda.upozorneni?.map(u => <p key={u} style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0.3rem 0 0' }}>{u}</p>)}
        </Karta>
      )}

      {rozbor.navrh && <NavrhHodnoty navrh={rozbor.navrh} nadpis="Předběžný návrh hodnoty" bezMetody />}

      <Karta nadpis={`Doplňující dotazy${prepocitavam ? ' · přepočítávám' : ''}`}>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-dim)', margin: '0 0 0.8rem' }}>
          Ptáme se jen na to, co v podkladech chybí a mění volbu metody nebo výpočet.
          Povinné dotazy jsou označené hvězdičkou.
        </p>
        {rozbor.dotazy.map(d => (
          <div key={d.klic} className="form-group" style={{ marginBottom: '1rem' }}>
            <label>{d.otazka}{d.povinny ? ' *' : ''}</label>
            <Odpoved dotaz={d} hodnota={odpovedi[d.klic]} onZmena={h => onZmena(d.klic, h)} />
            <small>{d.proc}{d.podminka ? ` Platí ${d.podminka}.` : ''}</small>
            {citace[d.klic] && (
              <small style={{ display: 'block', color: 'var(--purple-glow)' }}>
                Převzato ze zadání: „{citace[d.klic]}“. Zkontrolujte prosím.
              </small>
            )}
            {chybejiciPovinne.includes(d.klic) && <small className="chyba-pole">Tento dotaz je potřeba vyplnit.</small>}
          </div>
        ))}
      </Karta>
    </div>
  );
}
